import styled from '@emotion/styled';
import React from 'react';
import { useTranslation } from 'react-i18next';

import bannerImg from 'src/public/img/banner-bg.svg';

import { color, font, mediaQueries } from '../styles/variables';
import { Button } from './Button';

interface WrapperProps {
    centerContent: boolean;
    fullHeight: boolean;
}

const Wrapper = styled.div<WrapperProps>(({ centerContent, fullHeight }) => ({
    padding: 30,
    [mediaQueries.xs]: {
        padding: '20px 10px',
    },
    paddingLeft: 10,
    borderRadius: 8,
    backgroundImage: `url(${bannerImg})`,
    backgroundSize: 'cover',
    display: 'inline-flex',
    justifyContent: centerContent ? 'center' : 'inherit',
    alignItems: 'center',
    width: '100%',
    ...(fullHeight ? { height: '100%' } : { height: 320 }),
}));

const ContentWrapper = styled.div({
    padding: '0 10px',
});

const TryTodayText = styled.span(
    {
        backgroundColor: 'rgba(163, 194, 255, 0.38)',
        padding: '8px 8px 6px',
        color: color.white,
        borderRadius: 5,
    },
    font.heavy(font.size.small),
);

const Header = styled.h2(
    {
        color: color.white,
        marginTop: 20,
        marginBottom: 10,
    },
    font.black(font.size.extraLarge),
);

const Description = styled.p(
    {
        color: color.white,
        lineHeight: '28px',
        marginBottom: 22,
    },
    font.roman(font.size.large),
);

interface Props {
    title?: string;
    description?: string;
    buttonText?: string;
    wrapChildren?: boolean;
    onClick?: () => void;
    href?: string;
    to?: string;
    showTryTodayText?: boolean;
    centerContent?: boolean;
    fullHeight?: boolean;
}

export enum BannerTestID {
    Header = 'BannerTestID.Header',
    Description = 'BannerTestID.Description',
    CtaButton = 'BannerTestID.CtaButton',
}

export const Banner: React.FC<Props> = ({
    title,
    description,
    buttonText,
    showTryTodayText,
    children,
    wrapChildren = true,
    onClick,
    href,
    to,
    centerContent = false,
    fullHeight = false,
}) => {
    const { t } = useTranslation();
    const buttonProps = {
        testID: BannerTestID.CtaButton,
        rounded: true,
        horizontalPadding: 'small',
        type: 'white2',
    } as const;

    return (
        <Wrapper
            centerContent={centerContent}
            fullHeight={fullHeight}
            data-testid="banner"
            className="banner__wrapper"
        >
            <ContentWrapper className="banner__contentWrapper">
                {showTryTodayText ? (
                    <TryTodayText>{t('shared:banner.try_today')}</TryTodayText>
                ) : null}
                {title ? <Header data-testid={BannerTestID.Header}>{title}</Header> : null}
                {description ? (
                    <Description
                        data-testid={BannerTestID.Description}
                        className="banner__description"
                    >
                        {description}
                    </Description>
                ) : null}
                {buttonText ? (
                    to ? (
                        <Button {...buttonProps} to={to}>
                            {buttonText}
                        </Button>
                    ) : href ? (
                        <Button {...buttonProps} href={href} target="_blank">
                            {buttonText}
                        </Button>
                    ) : onClick ? (
                        <Button {...buttonProps} onClick={onClick}>
                            {buttonText}
                        </Button>
                    ) : (
                        // TODO(evanmh): Consider loggined a warning if no interaction
                        // is set.
                        <Button {...buttonProps} onClick={() => undefined}>
                            {buttonText}
                        </Button>
                    )
                ) : null}
                {children && wrapChildren ? children : null}
            </ContentWrapper>
            {children && !wrapChildren ? children : null}
        </Wrapper>
    );
};
